"use client"

import { colors } from "@/ui/theme/colors"
import { mediaQueries } from "@/ui/theme/mediaQueries"
import styled from "@emotion/styled"
import { Box, TextField, Typography } from "@mui/material"
import { useRef } from "react"
import { Control, Controller, FieldValues, Path } from "react-hook-form"

const StyledDateContainer = styled(Box)`
  display: flex;
  gap: 8px;
  width: 100%;
  direction: rtl; /* Ensure LTR layout */
  ${mediaQueries.desktopM} {
    padding-top: 24px;
  }
`

const StyledDateInputWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`

const StyledDateLabel = styled(Typography)`
  font-weight: 500;
  font-size: 14px;
  text-align: center;
  margin-top: 4px;
  color: ${colors.black};
`

const StyledSeparator = styled(Typography)`
  color: ${colors.red};
  font-weight: bold;
  font-size: 18px;
  display: flex;
  align-items: center;
  margin: 0 8px;
  padding-bottom: 24px;
`

const StyledTextField = styled(TextField)`
  & .MuiInputBase-input {
    text-align: center;
  }
`

interface CustomDatePickerProps<T extends FieldValues> {
  control: Control<T>
  dayName: Path<T>
  monthName: Path<T>
  yearName: Path<T>
  dayPlaceholder?: string
  monthPlaceholder?: string
  yearPlaceholder?: string
  dayLabel: string
  monthLabel: string
  yearLabel: string
  disabled?: boolean
  errors: {
    [key: string]: any
  }
}

// Handle number-only input for date fields
const handleNumberInput = (value: string, maxLength: number) => {
  return value.replace(/\D/g, "").slice(0, maxLength)
}

// Helper function to validate if date is not in the future
const validateDateNotInFuture = (day: string, month: string, year: string) => {
  if (!day || !month || !year) return true // Skip validation if any field is empty

  const selectedDate = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
  )
  const today = new Date()
  today.setHours(23, 59, 59, 999) // Set to end of today to allow today's date

  return selectedDate <= today
}

const CustomDatePicker = <T extends FieldValues>({
  control,
  dayName,
  monthName,
  yearName,
  dayPlaceholder = "00",
  monthPlaceholder = "00",
  yearPlaceholder = "0000",
  dayLabel,
  monthLabel,
  yearLabel,
  disabled = false,
  errors,
}: CustomDatePickerProps<T>) => {
  const dayRef = useRef<HTMLInputElement>(null)
  const monthRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)

  const handleAutoFocus = (
    value: string,
    maxLength: number,
    nextRef?: React.RefObject<HTMLInputElement>,
  ) => {
    if (value.length === maxLength && nextRef?.current) {
      nextRef.current.focus()
    }
  }

  return (
    <StyledDateContainer>
      {/* Day input - first in LTR order */}
      <StyledDateInputWrapper>
        <Controller
          name={dayName}
          control={control}
          rules={{
            required: "חובה",
            validate: {
              validDay: (value, formValues) => {
                const day = parseInt(value, 10)
                if (day < 1 || day > 31) return "יום לא תקין"

                // Check if complete date is not in the future
                const month = formValues[monthName as string]
                const year = formValues[yearName as string]
                if (!validateDateNotInFuture(value, month, year)) {
                  return "לא ניתן לבחור תאריך עתידי"
                }

                return true
              },
            },
          }}
          render={({ field }) => (
            <StyledTextField
              {...field}
              inputRef={dayRef}
              placeholder={"00"}
              onChange={(e) => {
                const newValue = handleNumberInput(e.target.value, 2)
                field.onChange(newValue)
                handleAutoFocus(newValue, 2, monthRef)
              }}
              variant="outlined"
              error={!!errors[dayName]}
              helperText={errors[dayName]?.message}
              disabled={disabled}
              InputProps={{
                readOnly: disabled,
              }}
            />
          )}
        />
        <StyledDateLabel>{dayLabel}</StyledDateLabel>
      </StyledDateInputWrapper>

      <StyledSeparator>/</StyledSeparator>

      {/* Month input - second in LTR order */}
      <StyledDateInputWrapper>
        <Controller
          name={monthName}
          control={control}
          rules={{
            required: "חובה",
            validate: {
              validMonth: (value, formValues) => {
                const month = parseInt(value, 10)
                if (month < 1 || month > 12) return "חודש לא תקין"

                // Check if complete date is not in the future
                const day = formValues[dayName as string]
                const year = formValues[yearName as string]
                if (!validateDateNotInFuture(day, value, year)) {
                  return "לא ניתן לבחור תאריך עתידי"
                }

                return true
              },
            },
          }}
          render={({ field }) => (
            <StyledTextField
              {...field}
              inputRef={monthRef}
              placeholder={"00"}
              onChange={(e) => {
                const newValue = handleNumberInput(e.target.value, 2)
                field.onChange(newValue)
                handleAutoFocus(newValue, 2, yearRef)
              }}
              variant="outlined"
              error={!!errors[monthName]}
              helperText={errors[monthName]?.message}
              disabled={disabled}
              InputProps={{
                readOnly: disabled,
              }}
            />
          )}
        />
        <StyledDateLabel>{monthLabel}</StyledDateLabel>
      </StyledDateInputWrapper>

      <StyledSeparator>/</StyledSeparator>

      {/* Year input - third in LTR order */}
      <StyledDateInputWrapper>
        <Controller
          name={yearName}
          control={control}
          rules={{
            required: "חובה",
            validate: {
              validYear: (value, formValues) => {
                const year = parseInt(value, 10)
                const currentYear = new Date().getFullYear()
                if (year < 2000 || year > currentYear) return "שנה לא תקינה"

                // Check if complete date is not in the future
                const day = formValues[dayName as string]
                const month = formValues[monthName as string]
                if (!validateDateNotInFuture(day, month, value)) {
                  return "לא ניתן לבחור תאריך עתידי"
                }

                return true
              },
            },
          }}
          render={({ field }) => (
            <StyledTextField
              {...field}
              inputRef={yearRef}
              placeholder={"0000"}
              onChange={(e) =>
                field.onChange(handleNumberInput(e.target.value, 4))
              }
              variant="outlined"
              error={!!errors[yearName]}
              helperText={errors[yearName]?.message}
              disabled={disabled}
              InputProps={{
                readOnly: disabled,
              }}
            />
          )}
        />
        <StyledDateLabel>{yearLabel}</StyledDateLabel>
      </StyledDateInputWrapper>
    </StyledDateContainer>
  )
}

export default CustomDatePicker
